import React, { useState, useMemo } from 'react';
import { AlertTriangle, Info, X, ChevronRight } from 'lucide-react';

// Types
export type RiskSeverity = 'critical' | 'high' | 'medium' | 'low' | 'none';

export interface RiskItem {
  id: string;
  description: string;
  severity: RiskSeverity;
  cfrCitation?: string;
  clauseReference?: string;
}

export interface ArticleRisk {
  articleId: string;
  articleNumber: string;
  articleTitle: string;
  risks: RiskItem[];
  riskCounts: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  overallSeverity: RiskSeverity;
}

export interface RiskHeatMapProps {
  articles: ArticleRisk[];
  onArticleClick?: (article: ArticleRisk) => void;
  onRiskClick?: (risk: RiskItem, article: ArticleRisk) => void;
  testId?: string;
}

// Helper function to get severity color classes - using design system colors
const getSeverityColors = (severity: RiskSeverity): { bg: string; border: string; text: string; hoverBg: string } => {
  switch (severity) {
    case 'critical':
      return {
        bg: 'bg-danger-500',
        border: 'border-danger-600',
        text: 'text-white',
        hoverBg: 'hover:bg-danger-600',
      };
    case 'high':
      return {
        bg: 'bg-warning-500',
        border: 'border-warning-600',
        text: 'text-white',
        hoverBg: 'hover:bg-warning-600',
      };
    case 'medium':
      return {
        bg: 'bg-warning-400',
        border: 'border-warning-500',
        text: 'text-neutral-900',
        hoverBg: 'hover:bg-warning-500',
      };
    case 'low':
      return {
        bg: 'bg-success-500',
        border: 'border-success-600',
        text: 'text-white',
        hoverBg: 'hover:bg-success-600',
      };
    case 'none':
    default:
      return {
        bg: 'bg-neutral-100',
        border: 'border-neutral-200',
        text: 'text-neutral-400',
        hoverBg: 'hover:bg-neutral-200',
      };
  }
};

// Get severity label
const getSeverityLabel = (severity: RiskSeverity): string => {
  switch (severity) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    case 'none':
    default:
      return 'No Risks';
  }
};

// Tooltip component
interface TooltipProps {
  article: ArticleRisk;
  position: { x: number; y: number };
  onClose: () => void;
  onViewDetails: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const Tooltip: React.FC<TooltipProps> = ({ article, position, onClose, onViewDetails, onMouseEnter, onMouseLeave }) => {
  const totalRisks = article.riskCounts.critical + article.riskCounts.high + article.riskCounts.medium + article.riskCounts.low;

  return (
    <div
      className="chart-tooltip fixed z-50 w-80"
      style={{
        left: `${Math.min(position.x, window.innerWidth - 340)}px`,
        top: `${Math.min(position.y + 10, window.innerHeight - 300)}px`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid="risk-heatmap-tooltip"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="chart-tooltip-header">Article {article.articleNumber}</h4>
          <p className="text-sm text-neutral-600 line-clamp-1">{article.articleTitle}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-neutral-100 rounded transition-colors"
          data-testid="tooltip-close"
        >
          <X className="h-4 w-4 text-neutral-400" />
        </button>
      </div>

      {totalRisks > 0 ? (
        <>
          <div className="mb-3">
            <p className="text-sm font-medium text-neutral-700 mb-2">
              {totalRisks} Risk{totalRisks !== 1 ? 's' : ''} Identified
            </p>
            <div className="grid grid-cols-4 gap-2 text-center">
              {article.riskCounts.critical > 0 && (
                <div className="bg-danger-100 rounded-lg p-2" data-testid="tooltip-critical-count">
                  <div className="text-lg font-bold text-danger-700">{article.riskCounts.critical}</div>
                  <div className="text-xs text-danger-600">Critical</div>
                </div>
              )}
              {article.riskCounts.high > 0 && (
                <div className="bg-warning-100 rounded-lg p-2" data-testid="tooltip-high-count">
                  <div className="text-lg font-bold text-warning-700">{article.riskCounts.high}</div>
                  <div className="text-xs text-warning-600">High</div>
                </div>
              )}
              {article.riskCounts.medium > 0 && (
                <div className="bg-warning-100 rounded-lg p-2" data-testid="tooltip-medium-count">
                  <div className="text-lg font-bold text-warning-600">{article.riskCounts.medium}</div>
                  <div className="text-xs text-warning-500">Medium</div>
                </div>
              )}
              {article.riskCounts.low > 0 && (
                <div className="bg-success-100 rounded-lg p-2" data-testid="tooltip-low-count">
                  <div className="text-lg font-bold text-success-700">{article.riskCounts.low}</div>
                  <div className="text-xs text-success-600">Low</div>
                </div>
              )}
            </div>
          </div>

          {/* Top risks preview */}
          {article.risks.length > 0 && (
            <div className="mb-3">
              <p className="chart-tooltip-label uppercase font-medium mb-2">Top Risks</p>
              <ul className="space-y-1">
                {article.risks.slice(0, 3).map((risk) => {
                  const colors = getSeverityColors(risk.severity);
                  return (
                    <li
                      key={risk.id}
                      className="flex items-start gap-2 text-sm"
                      data-testid={`tooltip-risk-${risk.id}`}
                    >
                      <span className={`flex-shrink-0 w-2 h-2 mt-1.5 rounded-full ${colors.bg}`} />
                      <span className="text-neutral-700 line-clamp-1">{risk.description}</span>
                    </li>
                  );
                })}
                {article.risks.length > 3 && (
                  <li className="text-xs text-neutral-500 pl-4">
                    +{article.risks.length - 3} more risks
                  </li>
                )}
              </ul>
            </div>
          )}

          <button
            onClick={onViewDetails}
            className="btn-primary w-full"
            data-testid="tooltip-view-details"
          >
            View Details
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      ) : (
        <div className="text-center py-4">
          <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Info className="h-5 w-5 text-success-600" />
          </div>
          <p className="text-sm text-neutral-600">No compliance risks identified in this article.</p>
        </div>
      )}
    </div>
  );
};

// Legend component
const Legend: React.FC = () => {
  const severities: RiskSeverity[] = ['critical', 'high', 'medium', 'low', 'none'];

  return (
    <div className="chart-legend" data-testid="risk-heatmap-legend">
      <span className="chart-legend-title">Risk Severity:</span>
      {severities.map((severity) => {
        const colors = getSeverityColors(severity);
        return (
          <div key={severity} className="chart-legend-item" data-testid={`legend-${severity}`}>
            <div className={`chart-legend-square ${colors.bg}`} />
            <span className="chart-legend-label">{getSeverityLabel(severity)}</span>
          </div>
        );
      })}
    </div>
  );
};

// Heat map cell component
interface HeatMapCellProps {
  article: ArticleRisk;
  onClick: () => void;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  isSelected: boolean;
}

const HeatMapCell: React.FC<HeatMapCellProps> = ({
  article,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected,
}) => {
  const colors = getSeverityColors(article.overallSeverity);
  const totalRisks =
    article.riskCounts.critical +
    article.riskCounts.high +
    article.riskCounts.medium +
    article.riskCounts.low;

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`
        relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all cursor-pointer
        ${colors.bg} ${colors.border} ${colors.text} ${colors.hoverBg}
        ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}
        min-h-[100px] min-w-[120px]
      `}
      data-testid={`heatmap-cell-${article.articleId}`}
      aria-label={`Article ${article.articleNumber}: ${article.articleTitle}. ${totalRisks} risks, highest severity: ${getSeverityLabel(article.overallSeverity)}`}
    >
      <div className="text-lg font-bold">Art. {article.articleNumber}</div>
      <div className="text-xs opacity-90 text-center line-clamp-2 mt-1">
        {article.articleTitle}
      </div>
      {totalRisks > 0 && (
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-xs font-semibold">{totalRisks}</span>
        </div>
      )}
    </button>
  );
};

// Main RiskHeatMap component
export const RiskHeatMap: React.FC<RiskHeatMapProps> = ({
  articles,
  onArticleClick,
  onRiskClick,
  testId = 'risk-heatmap',
}) => {
  const [hoveredArticle, setHoveredArticle] = useState<ArticleRisk | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isTooltipHoveredRef = React.useRef(false);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return articles.reduce(
      (acc, article) => ({
        totalRisks:
          acc.totalRisks +
          article.riskCounts.critical +
          article.riskCounts.high +
          article.riskCounts.medium +
          article.riskCounts.low,
        critical: acc.critical + article.riskCounts.critical,
        high: acc.high + article.riskCounts.high,
        medium: acc.medium + article.riskCounts.medium,
        low: acc.low + article.riskCounts.low,
      }),
      { totalRisks: 0, critical: 0, high: 0, medium: 0, low: 0 }
    );
  }, [articles]);

  const handleCellMouseEnter = (article: ArticleRisk, e: React.MouseEvent) => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setHoveredArticle(article);
    setTooltipPosition({ x: e.clientX, y: e.clientY });
  };

  const handleCellMouseLeave = () => {
    // Delay hiding to allow mouse to travel to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHoveredRef.current) {
        setHoveredArticle(null);
      }
    }, 150);
  };

  const handleTooltipMouseEnter = () => {
    // Clear hide timeout when entering tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    isTooltipHoveredRef.current = true;
  };

  const handleTooltipMouseLeave = () => {
    isTooltipHoveredRef.current = false;
    setHoveredArticle(null);
  };

  const handleCellClick = (article: ArticleRisk) => {
    setSelectedArticle(article.articleId);
    onArticleClick?.(article);
  };

  const handleTooltipClose = () => {
    isTooltipHoveredRef.current = false;
    setHoveredArticle(null);
  };

  const handleViewDetails = () => {
    if (hoveredArticle) {
      onArticleClick?.(hoveredArticle);
      setHoveredArticle(null);
    }
  };

  if (articles.length === 0) {
    return (
      <div
        className="chart-container chart-empty"
        data-testid={`${testId}-empty`}
      >
        <div className="chart-empty-icon">
          <AlertTriangle className="h-8 w-8 text-neutral-400" />
        </div>
        <h3 className="chart-empty-title">No Risk Data Available</h3>
        <p className="chart-empty-description">
          Run a risk analysis on your contracts to view the risk heat map.
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container" data-testid={testId}>
      {/* Header */}
      <div className="chart-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="chart-title">
              <AlertTriangle className="h-5 w-5 text-warning-500" />
              Risk Heat Map
            </h3>
            <p className="chart-subtitle">
              Click on an article to view detailed risk information
            </p>
          </div>

          {/* Summary stats */}
          <div className="chart-summary" data-testid="heatmap-summary">
            <span className="chart-summary-stat">
              <span className="chart-summary-value">{summaryStats.totalRisks}</span> Total Risks
            </span>
            <div className="flex items-center gap-2">
              {summaryStats.critical > 0 && (
                <span className="chart-badge-critical" data-testid="summary-critical">
                  {summaryStats.critical} Critical
                </span>
              )}
              {summaryStats.high > 0 && (
                <span className="chart-badge-high" data-testid="summary-high">
                  {summaryStats.high} High
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Heat map grid */}
      <div className="chart-content">
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(140px, 1fr))`,
          }}
          data-testid="heatmap-grid"
        >
          {articles.map((article) => (
            <HeatMapCell
              key={article.articleId}
              article={article}
              onClick={() => handleCellClick(article)}
              onMouseEnter={(e) => handleCellMouseEnter(article, e)}
              onMouseLeave={handleCellMouseLeave}
              isSelected={selectedArticle === article.articleId}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="chart-footer">
        <Legend />
      </div>

      {/* Tooltip */}
      {hoveredArticle && (
        <Tooltip
          article={hoveredArticle}
          position={tooltipPosition}
          onClose={handleTooltipClose}
          onViewDetails={handleViewDetails}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </div>
  );
};

export default RiskHeatMap;
